import { useState, useEffect, useRef, useContext } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import VisitScheduleAccordion from "../TrialAccordions/VisitScheduleAccordion";
import AllBlocksAccordion from "../TrialAccordions/AllBlocksAccordion";
import SpecialVisitSession from "../Ecrf/SpecialVisitSession";
import VisitNameAccordion from "./VisitNameAccordion";
import ScheduleVisits from "./ScheduleVisits";
import DuplicateVisitDialog from "./DuplicateVisitDialog";
import CreateVisitDialog from "./CreateVisitDialog";
import TabsWithDropdown from "./TabsWithDropDown";
import EDiaryTab from "./EDiaryTab";
import PatientEdiaryPanel from "../../Pages/Patient/PatientEdiaryPanel";
import getCustomNames from "../../utils/customNames";
import exportEcrfSourceDoc from "../../utils/exportEcrfSourceDoc";
import { LoaderContext } from "../../context/LoaderContext";
import Loader from "../Loader";
import logUserAction from "../../apis/userLogger";
import { useEcrfContext } from "../../context/EcrfContext";
import useUpdateVisitMutation from "../../hooks/useUpdateVisitMutation";
import useVisitPhotoTypes from "../../hooks/useVisitPhotoTypes";
import useGetVisitBlocks from "../../hooks/useGetVisitBlocks";
import { useInitialIsFetching } from "../../hooks/useInitialIsFetching";
import useGetPerVisitEcrfBlocks from "../../hooks/useGetPerVisitEcrfBlocks";
import useUpdatePerVisitEcrfBlocksMutation from "../../hooks/useUpdatePerVisitEcrfBlocksMutation";
import useUserActionsLogsMutation from "../../hooks/useUserActionsLogsMutation";
import { CRO_RELATED_ROLES, USER_ROLES } from "../../utils/constants";
import { isTrialConfigFinalized } from "../../utils/helpers";
import useGetSinglePatient from "../../hooks/useGetSinglePatient";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

export default function BasicTabs({
  setMessage,
  setTypeOfMessage,
  setModalVisitChangeShow,
  modalVisitChangeShow,
  value,
  setValue,
  numberOfVisitsArray,
  setNumberOfVisitsArray,
  allSurveys,
  setScheduleBaselineModalShow,
  singlePhotoSession,
}) {
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;
  const isInitialFetching = useInitialIsFetching();

  const {
    selectedVisit,
    setSelectedVisit,
    zeroVisit,
    eventList,
    setInitialFlag,
    trial,
    patientId,
    selectedSessionId,
    setSelectedSessionId,
    allVisits,
    hasChanged,
    setHasChanged,
    setPhotoTypes,
    setLocalBlocks,
    surveyData,
    setSurveyData,
    hasEcrfBlocksChanged,
    setHasEcrfBlocksChanged,
    isAllEcrfBlocks,
    setIsAllEcrfBlocks,
  } = useEcrfContext();

  const isEdiaryTab = selectedVisit?.visit_number === -6;
  const isTrialLocked = ["Completed", "Archived"].includes(trial?.status);
  // Per-patient edits stay open on a finalized Live trial; visit configuration does not
  const isConfigLocked =
    isTrialLocked || (isTrialConfigFinalized(trial) && patientId === undefined);
  const canManageVisits =
    patientId === undefined && [...CRO_RELATED_ROLES, USER_ROLES.DATA_MANAGER].includes(role);
  const canDuplicateVisit =
    canManageVisits && selectedVisit?.id !== undefined && selectedVisit?.visit_number >= 0;
  // Duplicated visits are appended after the latest end of any visit, since visits may overlap
  const duplicateScheduleAnchor = Math.max(
    0,
    ...(allVisits ?? [])
      .filter((visit) => visit.visit_number >= 0)
      .map((visit) => visit.visit_schedule + visit.end_visit_schedule)
  );
  const [showDuplicateVisitDialog, setShowDuplicateVisitDialog] = useState(false);
  const [showCreateVisitDialog, setShowCreateVisitDialog] = useState(false);
  const pendingVisitNumberRef = useRef(null);

  const handleVisitsDuplicated = ({ renumbered }) => {
    if (renumbered?.length > 0) toast.info("Visits were renumbered to follow the schedule.");
  };

  const handleVisitCreated = ({ visit_number, renumbered }) => {
    pendingVisitNumberRef.current = visit_number;
    if (renumbered?.length > 0) toast.info("Visits were renumbered to follow the schedule.");
  };

  const { trialFilter } = useOutletContext() ?? {};

  const updateVisitMutation = useUpdateVisitMutation();
  const updatePerVisitEcrfBlocksMutation = useUpdatePerVisitEcrfBlocksMutation();

  const { data: infoData } = useGetSinglePatient({
    patientId,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  let baseline_name = "";
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [searchTrailType, setSearchTrailType] = useState("");
  const [searchBlockName, setSearchBlockName] = useState("");
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [endDays, setEndDays] = useState(0);
  const [endHours, setEndHours] = useState(0);
  const [newForm, setNewForm] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [visitToSelect, setVisitToSelect] = useState({});
  const [selectedBlock, setSelectedBlock] = useState(
    role !== USER_ROLES.REVIEWER && "Patient Activity in this Visit"
  );
  const [hasUnsavedEcrfChanges, setHasUnsavedEcrfChanges] = useState(false);
  const [showEcrfUnsavedModal, setShowEcrfUnsavedModal] = useState(false);

  const prevSelectedVisitIdRef = useRef(undefined);
  const loaderContext = useContext(LoaderContext);

  const logAction = useUserActionsLogsMutation();

  const defaultVisit = {
    survey_form: "",
    extra_photos: [],
    app_videos: [],
    visit_schedule: 0,
    end_visit_schedule: 0,
    visit_number: 0,
    is_all_instruction: false,
    consent_form: {
      content: "",
      title: "",
      id: "",
    },
    visit_instructions: {
      contnt: "",
      contnt_de: "",
      contnt_es: "",
      id: "",
    },
    trial_block: [],
    disabled_photos: [],
    start_date: "",
    trial_type: "",
  };

  const handleTabChange = (event, newValue, index) => {
    setSelectedSessionId(0);
    setVisitToSelect({ event: event, newValue: newValue, index: index });

    if (hasChanged) {
      setModalVisitChangeShow(true);
    } else {
      setValue(index);
      if (role === USER_ROLES.REVIEWER) {
        setSelectedBlock("");
      } else {
        setSelectedBlock("Patient Activity in this Visit");
      }
      setNewForm(false);

      const filteredVisit = allVisits?.filter((visit) => {
        return visit.visit_number === newValue;
      });

      if (filteredVisit.length > 0) {
        setSelectedVisit({
          ...filteredVisit[0],
          start_date: new Date(filteredVisit[0].start_date + "Z"),
        });
        logAction.mutate({
          action_type: `Switched the visit to ${filteredVisit[0]?.visit_name}`,
          trial_name: trial.name,
          patient_id: patientId,
        });
      } else {
        if (!("visit_name" in defaultVisit)) defaultVisit["visit_name"] = getCustomNames(newValue);

        if (newValue !== 0 && newValue !== -1 && newValue !== -2) {
          setSelectedVisit({
            ...defaultVisit,
            visit_number: newValue,
            extra_photos: zeroVisit?.extra_photos,
            app_videos: zeroVisit?.app_videos,
            visit_instructions: zeroVisit?.visit_instructions,
          });

          logAction.mutate({
            action_type: `Switched the visit to ${defaultVisit?.visit_name}`,
            trial_name: trial.name,
            patient_id: patientId,
          });
        } else {
          setSelectedVisit({ ...defaultVisit, visit_number: newValue });
          logAction.mutate({
            action_type: `Switched the visit to ${defaultVisit?.visit_name}`,
            trial_name: trial.name,
            patient_id: patientId,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (isMounted && !hasChanged) {
      if (visitToSelect?.index !== undefined) {
        handleTabChange(visitToSelect?.event, visitToSelect?.newValue, visitToSelect?.index);
      } else {
        handleTabChange(0, selectedVisit?.visit_number, 0);
      }
    } else {
      setIsMounted(true);
    }
  }, [hasChanged]);

  useEffect(() => {
    if (!numberOfVisitsArray.includes(0)) {
      setNumberOfVisitsArray([0, ...numberOfVisitsArray]);
    }
  }, [numberOfVisitsArray]);

  useEffect(() => {
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    if (hasChanged) {
      window.addEventListener("beforeunload", handler);

      return () => {
        window.removeEventListener("beforeunload", handler);
      };
    }
    return () => {};
  }, [hasChanged]);

  const handleDaysChange = (e) => {
    setHasChanged(true);
    const { value } = e.target;
    setDays(parseInt(value));
  };

  const handleHoursChange = (e) => {
    setHasChanged(true);
    const { value } = e.target;
    setHours(parseInt(value));
  };

  const handleDaysChangeEnd = (e) => {
    setHasChanged(true);
    const { value } = e.target;
    setEndDays(parseInt(value));
  };

  const handleHoursChangeEnd = (e) => {
    setHasChanged(true);
    const { value } = e.target;
    setEndHours(parseInt(value));
  };

  const handleVisitFieldChange = (e) => {
    setHasChanged(true);
    const { name, value } = e.target;

    if (name === "title" || name === "content") {
      setSelectedVisit((visit) => {
        return {
          ...visit,
          consent_form: {
            ...visit.consent_form,
            [name]: value,
          },
        };
      });
    } else if (name === "contnt") {
      setSelectedVisit((visit) => {
        return {
          ...visit,
          visit_instructions: {
            ...visit.visit_instructions,
            [name]: value,
          },
        };
      });
    } else if (name === "consent_list_id") {
      setSelectedVisit((visit) => {
        return {
          ...visit,
          consent_form: {
            ...visit.consent_form,
            id: value,
          },
        };
      });
    } else if (name === "survey_form") {
      setSelectedVisit((visit) => {
        return {
          ...visit,
          survey_form: { id: value },
        };
      });
    } else if (["Lesion 1", "Lesion 2", "Lesion 3"].includes(name)) {
      setSelectedVisit((visit) => {
        let current_extras = visit.extra_photos;
        const index = current_extras.indexOf(name);
        if (index > -1) current_extras.splice(index, 1);
        else current_extras.push(name);
        return {
          ...visit,
          extra_photos: current_extras,
        };
      });
    } else if (name === "app_videos") {
      setSelectedVisit((visit) => {
        let current_extras = visit.app_videos;
        const index = current_extras.indexOf(name);
        if (index > -1) current_extras.splice(index, 1);
        else current_extras.push(name);
        return {
          ...visit,
          app_videos: current_extras,
        };
      });
    } else {
      setSelectedVisit((visit) => {
        return {
          ...visit,
          [name]: value,
        };
      });
    }
  };

  const { data: photoTypesData } = useVisitPhotoTypes({
    trialId: trial?.id,
    visitId: patientId ? selectedVisit?.id : null,
    enabled: !!trial?.id && (!patientId || !!selectedVisit?.id),
  });

  useEffect(() => {
    if (photoTypesData) {
      setPhotoTypes(photoTypesData);
    }
  }, [photoTypesData]);

  const handleSelectBlock = (block, is_new_block) => {
    if (patientId && hasUnsavedEcrfChanges) {
      setShowEcrfUnsavedModal(true);
    } else if (patientId && hasChanged) {
      setModalVisitChangeShow(true);
    } else {
      setNewForm(is_new_block);
      setSelectedBlock(block);
    }
  };

  const handleVisitUpdate = async () => {
    if (!hasChanged && !hasEcrfBlocksChanged) {
      toast.info("Nothing to update.");
      return;
    }

    try {
      if (hasChanged) {
        const payload = {
          ...selectedVisit,
          visit_schedule: days * 24 + hours,
          end_visit_schedule: endDays * 24 + endHours,
        };

        const result = await updateVisitMutation.mutateAsync({
          trialId: trial?.id,
          patientId,
          payload,
        });

        if (result?.renumbered?.length > 0) {
          if (result.visit_number !== selectedVisit?.visit_number) {
            setSelectedVisit((prev) => ({ ...prev, visit_number: result.visit_number }));
            const newIndex = numberOfVisitsArray.indexOf(result.visit_number);
            if (newIndex !== -1) setValue(newIndex);
          }
          toast.info("Visits were renumbered to follow the schedule.");
        }
      }

      if (hasEcrfBlocksChanged) {
        const result = await updatePerVisitEcrfBlocksMutation.mutateAsync({
          visitId: selectedVisit?.id,
          ecrf_blocks: surveyData,
          isAllEcrfBlocks: patientId ? undefined : isAllEcrfBlocks,
        });

        result?.skipped?.forEach(({ visit_number, block_name }) => {
          toast.warning(
            `"${block_name}" was kept on visit ${visit_number} because patients already have answers on it.`
          );
        });
      }

      setHasChanged(false);
      setHasEcrfBlocksChanged(false);

      logAction.mutate({
        action_type: `Visit Info updated, Visit: ${selectedVisit?.visit_name}, ${
          "Photos: " +
          selectedVisit?.extra_photos?.map((p) => p.value).join(", ") +
          ", Videos: " +
          selectedVisit?.app_videos?.map((p) => p.value).join(", ") +
          ", Assessment Areas: " +
          selectedVisit?.assessment_areas?.map((p) => p.value).join(", ")
        }`,
        trial_name: trial.name,
        patient_id: patientId,
      });

      toast.success(`Visit updated successfully.`);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Visit update failed.";
      toast.error(msg);
    }
  };

  const { data: localBlocksData } = useGetVisitBlocks({
    trialType: searchTrailType,
    blockName: searchBlockName,
    enabled: window.location.href.includes("clinic_trials"),
  });

  useEffect(() => {
    if (localBlocksData) {
      setLocalBlocks(localBlocksData);
    }
  }, [localBlocksData]);

  const handleNumberOfVisitsArray = () => {
    const visitNumbers = [
      ...new Set((allVisits ?? []).map((visit) => visit.visit_number).filter((n) => n >= 0)),
    ].sort((a, b) => a - b);
    const screeningEvent = eventList?.filter((event) => event === -3);
    if (screeningEvent?.length > 0) {
      const eventsWithoutScreeing = eventList?.filter((event) => event !== -3);
      setNumberOfVisitsArray([-3, ...visitNumbers, ...eventsWithoutScreeing]);
    } else {
      setNumberOfVisitsArray([...visitNumbers, ...eventList]);
    }
  };

  useEffect(() => {
    handleNumberOfVisitsArray();
  }, [allVisits, eventList]);

  useEffect(() => {
    const pendingVisitNumber = pendingVisitNumberRef.current;
    if (pendingVisitNumber === null) return;
    const index = numberOfVisitsArray.indexOf(pendingVisitNumber);
    if (index === -1 || !allVisits?.some((visit) => visit.visit_number === pendingVisitNumber)) {
      return;
    }
    pendingVisitNumberRef.current = null;
    handleTabChange(null, pendingVisitNumber, index);
  }, [numberOfVisitsArray, allVisits]);

  useEffect(() => {
    setInitialFlag(true);
  }, []);

  const getDaysHours = () => {
    const GetDays = selectedVisit?.visit_schedule / 24;
    const days = Math.floor(GetDays);
    setDays(days);
    const GetHours = selectedVisit?.visit_schedule % 24;
    const hours = Math.floor(GetHours);
    setHours(hours);
  };

  useEffect(() => {
    getDaysHours();
  }, [selectedVisit?.visit_schedule]);

  const getDaysHoursEnd = () => {
    const GetDays = selectedVisit?.end_visit_schedule / 24;
    const endDays = Math.floor(GetDays);
    setEndDays(endDays);
    const GetHours = selectedVisit?.end_visit_schedule % 24;
    const endHours = Math.floor(GetHours);
    setEndHours(endHours);
  };

  useEffect(() => {
    getDaysHoursEnd();
  }, [selectedVisit?.end_visit_schedule]);

  useEffect(() => {
    if (selectedVisit?.id !== undefined) {
      prevSelectedVisitIdRef.current = selectedVisit?.id;
    }
  }, [selectedVisit]);

  const { data: perVisitLocalBlocksData } = useGetPerVisitEcrfBlocks({
    visitId: selectedVisit.id,
    patientId: patientId,
    sessionId: selectedSessionId,
    enabled: patientId
      ? (selectedVisit?.visit_number !== -2 && selectedVisit?.visit_number !== -1) ||
        Boolean(selectedSessionId)
      : Boolean(selectedVisit.id),
  });

  useEffect(() => {
    if (perVisitLocalBlocksData) {
      setSurveyData(perVisitLocalBlocksData?.results);
      setIsAllEcrfBlocks(!!perVisitLocalBlocksData?.is_all_ecrf_blocks);
    }
  }, [perVisitLocalBlocksData]);

  const savePdf = function (includeAnswers, includeIds = true) {
    setIsLoadingPdf(true);
    try {
      exportEcrfSourceDoc({
        surveyData,
        visit: selectedVisit,
        trial,
        infoData,
        includeAnswers,
        includeIds,
      });
    } finally {
      setIsLoadingPdf(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: !!patientId ? 2 : 0,
      }}
    >
      {canDuplicateVisit && (
        <DuplicateVisitDialog
          open={showDuplicateVisitDialog}
          onClose={() => setShowDuplicateVisitDialog(false)}
          trialId={trial?.id}
          sourceVisit={selectedVisit}
          scheduleAnchor={duplicateScheduleAnchor}
          onDuplicated={handleVisitsDuplicated}
        />
      )}

      {canManageVisits && (
        <CreateVisitDialog
          open={showCreateVisitDialog}
          onClose={() => setShowCreateVisitDialog(false)}
          trialId={trial?.id}
          onCreated={handleVisitCreated}
        />
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            py: 1,
          }}
        >
          {!isEdiaryTab && (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  savePdf(false);
                  logUserAction('Clicked "Export as Source Doc"', "eCRF Visit Tabs");
                }}
                disabled={isLoadingPdf || loaderContext.loaderCount > 0 || isInitialFetching > 0}
                startIcon={isLoadingPdf ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Export as Source Doc
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  savePdf(true, false);
                  logUserAction('Clicked "Export as Filled Source Doc"');
                }}
                disabled={isLoadingPdf || loaderContext.loaderCount > 0 || isInitialFetching > 0}
                startIcon={isLoadingPdf ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Export as Filled Source Doc
              </Button>
            </>
          )}

          {canManageVisits && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowCreateVisitDialog(true)}
              data-testid="create-visit-btn"
              disabled={isConfigLocked || hasChanged || hasEcrfBlocksChanged}
              title={
                hasChanged || hasEcrfBlocksChanged
                  ? "Save your changes to this visit before creating a new one"
                  : undefined
              }
            >
              Create Visit
            </Button>
          )}

          {canDuplicateVisit && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowDuplicateVisitDialog(true)}
              data-testid="duplicate-visit-btn"
              disabled={isConfigLocked || hasChanged || hasEcrfBlocksChanged}
              title={
                hasChanged || hasEcrfBlocksChanged
                  ? "Save your changes to this visit before duplicating it"
                  : undefined
              }
            >
              Duplicate Visit
            </Button>
          )}

          {((role === "CRO Admin" && patientId === undefined) ||
            (role === "Cro_coordinator" && patientId === undefined) ||
            (role === "Data Manager" && patientId === undefined) ||
            role === "Investigator" ||
            (role === "Inv_coordinator" && patientId !== undefined)) && (
            <>
              {selectedBlock && !isEdiaryTab && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleVisitUpdate()}
                  disabled={(!hasChanged && !hasEcrfBlocksChanged) || isConfigLocked}
                >
                  Update Visit Info
                </Button>
              )}
            </>
          )}
        </Box>

        {allVisits?.length > 0 && (
          <TabsWithDropdown
            numberOfVisitsArray={numberOfVisitsArray}
            handleTabChange={handleTabChange}
            value={value}
            setValue={setValue}
          />
        )}
      </Box>

      <>
        {!(allVisits?.length > 0) ? (
          <Loader />
        ) : (
          numberOfVisitsArray.map((number, index) => {
            // Panels render for the selected visit, not for the number at their position: the tab
            // strip re-sorts numberOfVisitsArray in place, so position is not a reliable key here.
            if (isEdiaryTab) {
              return (
                <TabPanel key={number} value={value} index={index}>
                  {patientId ? (
                    <PatientEdiaryPanel
                      patientId={patientId}
                      trialId={trial?.id}
                      isTrialLocked={isTrialLocked}
                    />
                  ) : (
                    <EDiaryTab trialId={trial?.id} isLocked={isConfigLocked} />
                  )}
                </TabPanel>
              );
            }

            return (
              <TabPanel key={number} value={value} index={index}>
                <VisitNameAccordion
                  handleVisitFieldChange={handleVisitFieldChange}
                  isLocked={isConfigLocked}
                />

                {patientId &&
                  selectedVisit?.visit_number !== -4 &&
                  selectedVisit?.visit_number !== -2 &&
                  selectedVisit?.visit_number !== -1 &&
                  role !== USER_ROLES.REVIEWER && (
                    <ScheduleVisits
                      setScheduleBaselineModalShow={setScheduleBaselineModalShow}
                      singlePhotoSession={singlePhotoSession}
                    />
                  )}

                {selectedVisit?.visit_number !== -1 &&
                  selectedVisit?.visit_number !== -2 &&
                  selectedVisit?.visit_number !== -4 &&
                  role !== USER_ROLES.REVIEWER &&
                  role !== USER_ROLES.INVESTIGATOR &&
                  role !== USER_ROLES.CRC &&
                  role !== USER_ROLES.SUB_INVESTIGATOR && (
                    <VisitScheduleAccordion
                      role={role}
                      days={days}
                      hours={hours}
                      endDays={endDays}
                      endHours={endHours}
                      number={selectedVisit?.visit_number}
                      handleDaysChange={handleDaysChange}
                      handleHoursChange={handleHoursChange}
                      handleDaysChangeEnd={handleDaysChangeEnd}
                      handleHoursChangeEnd={handleHoursChangeEnd}
                      baseline_name={baseline_name}
                      isLocked={isConfigLocked}
                    />
                  )}

                {(selectedVisit?.visit_number === -1 && patientId) ||
                (selectedVisit?.visit_number === -2 && patientId) ? (
                  <SpecialVisitSession
                    selectedBlock={selectedBlock}
                    setSelectedBlock={setSelectedBlock}
                    handleSelectBlock={handleSelectBlock}
                    allSurveys={allSurveys}
                    setMessage={setMessage}
                    setTypeOfMessage={setTypeOfMessage}
                    modalVisitChangeShow={modalVisitChangeShow}
                    setModalVisitChangeShow={setModalVisitChangeShow}
                    searchTrailType={searchTrailType}
                    setSearchTrailType={setSearchTrailType}
                    searchBlockName={searchBlockName}
                    setSearchBlockName={setSearchBlockName}
                    newForm={newForm}
                    setNewForm={setNewForm}
                    setHasUnsavedEcrfChanges={setHasUnsavedEcrfChanges}
                    handleVisitUpdate={handleVisitUpdate}
                  />
                ) : (
                  <AllBlocksAccordion
                    selectedBlock={selectedBlock}
                    setSelectedBlock={setSelectedBlock}
                    handleSelectBlock={handleSelectBlock}
                    allSurveys={allSurveys}
                    handleVisitUpdate={handleVisitUpdate}
                    setMessage={setMessage}
                    setTypeOfMessage={setTypeOfMessage}
                    modalVisitChangeShow={modalVisitChangeShow}
                    setModalVisitChangeShow={setModalVisitChangeShow}
                    newForm={newForm}
                    setNewForm={setNewForm}
                    searchTrailType={searchTrailType}
                    setSearchTrailType={setSearchTrailType}
                    searchBlockName={searchBlockName}
                    setSearchBlockName={setSearchBlockName}
                    setHasUnsavedEcrfChanges={setHasUnsavedEcrfChanges}
                  />
                )}
              </TabPanel>
            );
          })
        )}
      </>

      {showEcrfUnsavedModal && (
        <Dialog
          open={showEcrfUnsavedModal}
          onClose={() => setShowEcrfUnsavedModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Unsaved Changes</DialogTitle>

          <DialogContent>
            <Typography align="center" sx={{ mt: 1 }}>
              You have unsaved changes, are you sure you want to switch to the other eCRF? All of
              your unsaved changes will be lost.
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                setHasUnsavedEcrfChanges(false);
                setShowEcrfUnsavedModal(false);
              }}
            >
              Discard Changes
            </Button>

            <Button variant="contained" onClick={() => setShowEcrfUnsavedModal(false)}>
              Continue Making Changes
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
